import { useFormik } from "formik";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { FC } from "react";
import { useRecoilValue } from "recoil";
import Button from "~/components/Button";
import DatePicker from "~/components/DatePicker";
import Input from "~/components/Input";
import PasswordInput from "~/components/Input/PasswordInput";
import Text from "~/components/Text";
import { useSignUp } from "~/data/auth/useSignUp";
import storeConfigAtom from "~/services/store-config/store-config.atom";
import { toastAlert } from "~/utils/toastify";
import { isValidEmail } from "~/utils/validators";
import Divider from "../Divider";
import SocialButtons from "../SocialButtons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import classNames from "classnames";
import { isUsingLayoutV2 } from "~/utils/theme";

dayjs.extend(utc);
interface SignUpFormProps {
  onSubmit?: (email: string) => void;
}

const SignUpForm: FC<SignUpFormProps> = () => {
  const router = useRouter();
  const initialValues = {
    email: "",
    phone_number: "",
    firstname: "",
    lastname: "",
    birthday: "",
    password: "",
    confirm_password: "",
  };
  const app = useRecoilValue(storeConfigAtom.store);

  const [createCustomer, { loading }] = useSignUp({
    onCompleted: () => {
      toastAlert({ type: "success", message: "Created customer successfully" });
      formik.resetForm({
        values: initialValues,
      });
      router.push("/sign-in");
    },
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validate: (values) => {
      const errors: Record<string, string> = {};

      if (!values.email?.trim()) {
        errors.email = "Please enter your email";
      } else if (!isValidEmail(values.email)) {
        errors.email = "Incorrect email format";
      }

      // if (!values.phone_number?.trim()) {
      //   errors.phone_number = "Please enter your phone number";
      // } else if (!isValidPhoneNumber(values.phone_number)) {
      //   errors.phone_number = "Incorrect phone number format";
      // }

      if (!values.firstname?.trim()) {
        errors.firstname = "Please enter your first name";
      }

      if (!values.lastname?.trim()) {
        errors.lastname = "Please enter your last name";
      }
      if (!values.birthday) {
        errors.birthday = "Please enter your birthday";
      } else if (isNaN(new Date(values.birthday).getTime())) {
        errors.birthday = "Please enter a valid date";
      }

      if (!values.password?.trim()) {
        errors.password = "Please enter your password";
      }

      if (!values.confirm_password?.trim()) {
        errors.confirm_password = "Please re-enter your password";
      } else if (values.password !== values.confirm_password) {
        errors.confirm_password = "Password mismatch";
      }

      return errors;
    },
    onSubmit: (values) => {
      const { email, phone_number, firstname, birthday, lastname, password } =
        values || {};
      createCustomer({
        variables: {
          input: {
            email,
            phone: phone_number,
            firstname: firstname,
            lastname: lastname,
            date_of_birth: dayjs(birthday),
            password,
            is_subscribed: false,
          },
        },
      });
    },
  });

  // const formatISO = (date: Date) => {
  //     if (!date) return '';
  //     return date.toISOString();
  // };

  return (
    <div className="flex flex-col justify-center flex-1 gap-5 px-5 lg:gap-8 lg:px-20">
      <div className="flex flex-col gap-2">
        <div
          className={classNames("font-semibold", {
            "text-center text-[32px]": isUsingLayoutV2,
          })}
        >
          {isUsingLayoutV2 ? "Create Account" : "Sign Up"}
        </div>
        {!isUsingLayoutV2 && (
          <Text>
            Already have an account?{" "}
            <Link href="/sign-in">
              <Text type="title-5" htmlTag="span" className="text-primary">
                Sign in
              </Text>
            </Link>
          </Text>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <div>
          {" "}
          <SocialButtons
            sociallogin_facebook_is_enabled={
              app.sociallogin_facebook_is_enabled
            }
            sociallogin_facebook_app_id={app.sociallogin_facebook_app_id}
            sociallogin_google_is_enabled={app.sociallogin_google_is_enabled}
            sociallogin_google_app_id={app.sociallogin_google_app_id}
          />
          {(!!app.sociallogin_facebook_is_enabled ||
            !!app.sociallogin_google_is_enabled) && (
            <Divider text="OR" className="my-2 mt-4" />
          )}
        </div>
        <Input
          label="Email"
          required
          placeholder="Enter your email"
          {...formik.getFieldProps("email")}
          error={formik.touched.email && formik.errors.email}
        />
        {/* <Input
          label="Phone number"
          required
          placeholder="Enter your phone number"
          {...formik.getFieldProps("phone_number")}
          error={formik.touched.phone_number && formik.errors.phone_number}
        /> */}
        <div className="flex flex-col gap-4 md:flex-row md:gap-2">
          <Input
            label="First name"
            required
            placeholder="Enter your first name"
            className="flex-1"
            {...formik.getFieldProps("firstname")}
            error={formik.touched.firstname && formik.errors.firstname}
          />
          <Input
            label="Last name"
            required
            placeholder="Enter your last name"
            className="flex-1"
            {...formik.getFieldProps("lastname")}
            error={formik.touched.lastname && formik.errors.lastname}
          />
        </div>
        <DatePicker
          label="Date of birth"
          required
          error={formik.touched.birthday && formik.errors.birthday}
          value={
            formik.values.birthday
              ? new Date(formik.values.birthday)
              : undefined
          }
          onChange={(e) => {
            formik?.setFieldValue("birthday", e);
          }}
        />
        <PasswordInput
          eyeIcon
          type="password"
          label="Password"
          required
          placeholder="Enter your password"
          {...formik.getFieldProps("password")}
          error={formik.touched.password && formik.errors.password}
          autoComplete="new-password"
        />
        <PasswordInput
          eyeIcon
          type="password"
          label="Confirm password"
          required
          placeholder="Re-enter your password"
          {...formik.getFieldProps("confirm_password")}
          error={
            formik.touched.confirm_password && formik.errors.confirm_password
          }
          autoComplete="new-password"
        />
      </div>
      <div className="flex flex-col gap-4">
        <Button
          className="justify-center"
          onClick={formik.submitForm}
          disabled={loading}
          loading={!!loading}
        >
          Sign up
        </Button>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Text type="body-1">
            By clicking Sign Up, you agree to our{" "}
            <Link href="/terms-of-services" className="text-primary-dark">
              <Text htmlTag="span" type="body-1" weight={500}>
                Terms of Service
              </Text>
            </Link>{" "}
            and that you have read our{" "}
            <Link href="/privacy-policy" className="text-primary-dark">
              <Text htmlTag="span" type="body-1" weight={500}>
                Privacy Policy
              </Text>
            </Link>
          </Text>
          {isUsingLayoutV2 && (
            <Text type="body-1">
              Already have an account?{" "}
              <Link href="/sign-in">
                <Text type="title-3" htmlTag="span" className="text-primary">
                  Sign in
                </Text>
              </Link>
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUpForm;
